import { Hardware } from '../../api/api-generated';
import {
  numbersMatch,
  stringIncludesIgnoreCase,
} from '../../utils/comparisons';
import { hasDatePassed } from '../../utils/dateTime';
import { HardwareItemFilter, HardwareItemForm } from './hardwareTypes';

export const filterHardwareItem = (
  item: Omit<Hardware, 'licenses'>,
  filters: HardwareItemFilter
): boolean => {
  return (
    // All the following conditions must be true to yield a true value
    (!filters.hardwareType ||
      numbersMatch(filters.hardwareType.id, item.hardwareTypeId)) &&
    (!filters.company ||
      item.companies
        .map((c) => c.companyName)
        .some((name) =>
          stringIncludesIgnoreCase(name, filters.company?.companyName)
        )) &&
    (!filters.serial ||
      stringIncludesIgnoreCase(item.serial, filters.serial)) &&
    (!filters.soldDateFrom ||
      item.order === null ||
      item.order.orderDate > filters.soldDateFrom) &&
    (!filters.soldDateTo ||
      item.order === null ||
      item.order.orderDate < filters.soldDateTo) &&
    (!filters.warrantyActive ||
      (filters.warrantyActive === 'None' && item.warrantyEndDate === null) ||
      (filters.warrantyActive === 'Active' &&
        item.warrantyEndDate !== null &&
        !hasDatePassed(new Date(item.warrantyEndDate))) ||
      (filters.warrantyActive === 'Expired' &&
        item.warrantyEndDate !== null &&
        hasDatePassed(new Date(item.warrantyEndDate))))
  );
};

export const filterConstraintChecker = (
  name: string,
  value: string,
  filters: HardwareItemFilter
) => {
  if (name === 'soldDateFrom') {
    if (filters.soldDateTo && filters.soldDateTo < value) return false;
  }
  if (name === 'soldDateTo') {
    if (filters.soldDateFrom && filters.soldDateFrom > value) return false;
  }
  return true;
};

export const hardwareHandlerMiddleware = (
  name: string,
  value: string,
  state: any,
  autoboxValue?: any,
  autoboxLabel?: string
) => {
  if (autoboxLabel?.startsWith('companies')) {
    const companyIndex = Number(
      autoboxLabel.slice(autoboxLabel.indexOf('s') + 1)
    );
    const companies = [...state.companies];
    companies[companyIndex] = autoboxValue;
    return { companies };
  } else if (autoboxLabel === 'hardwareType') {
    return { hardwareType: autoboxValue };
  } else if (autoboxLabel === 'department') {
    return { department: autoboxValue };
  } else if (autoboxLabel === 'inventoryHardware') {
    return { inventoryHardware: autoboxValue };
  } else if (autoboxLabel === 'order') {
    return { order: autoboxValue };
  } else if (name === 'warrantyStartDate') {
    // @ts-ignore
    if (state.warrantyEndDate && state.warrantyEndDate < value) {
      return { warrantyStartDate: state.warrantyStartDate };
    } else {
      return {};
    }
  } else if (name === 'warrantyEndDate') {
    // @ts-ignore
    if (state.warrantyStartDate > value) {
      return { warrantyEndDate: state.warrantyEndDate };
    } else {
      return {};
    }
  } else {
    return {};
  }
};

export const hardwareValidator = (item: any) => {
  if (!item.serial && !item.inventoryHardware) return false;
  if (!item.hardwareType) return false;
  if (item.companies.length < 1 || item.companies[0] === null) return false;
  if (!item.firmware) return false;
  return item.lptSold + '' === 'true' || item.lptSold + '' === 'false';
};

export const hardwareTransferValidator = (item: any) => {
  if (!item.hardwareType) return false;
  if (item.companies.length < 1) return false;
  if (!item.firmware) return false;
  return item.lptSold;
};

export const hardwareFormInitialState: HardwareItemForm = {
  companies: [],
  hardwareType: null,
  firmware: undefined,
  serial: null,
  notes: undefined,
  shipper: undefined,
  warrantyStartDate: undefined,
  warrantyEndDate: undefined,
  trackingNumber: undefined,
  order: null,
  lptSold: undefined,
  manufacturer: undefined,
  inventoryHardware: null,
  department: null,
};
